import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { TronWeb } from 'tronweb';

import type { TronLink } from '../types/tronweb';

interface TronContextType {
  tronWeb: TronWeb | null;
  address: string | null;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  balance: string | null;
  isLoadingBalance: boolean;
}

const TronContext = createContext<TronContextType>({
  tronWeb: null,
  address: null,
  isConnected: false,
  connect: async () => {},
  disconnect: () => {},
  balance: null,
  isLoadingBalance: false,
});

export const useTronWallet = () => useContext(TronContext);

interface TronProviderProps {
  children: ReactNode;
}

export const TronProvider = ({ children }: TronProviderProps): React.ReactNode => {
  const [tronWeb, setTronWeb] = useState<TronWeb | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [balance, setBalance] = useState<string | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  async function getTronWeb() {
    let tronWeb;
    const windowTronlink = (window as unknown as { tronLink?: TronLink })?.tronLink;
    if (!windowTronlink) {
      return null;
    }
    if (windowTronlink?.ready) {
      tronWeb = windowTronlink.tronWeb;
      return tronWeb;
    }

    const res = await windowTronlink.request({ method: 'tron_requestAccounts' });

    if (typeof res === 'string' && !res) {
      throw new Error('Please unlock TronLink wallet');
    }

    if (res.code === 4001) {
      throw new Error('User rejected the request.');
    }

    if (res.code === 200) {
      tronWeb = windowTronlink.tronWeb;
      return tronWeb;
    }

    tronWeb = windowTronlink.tronWeb;

    if (!tronWeb) {
      // eslint-disable-next-line no-console
      console.warn('Failed to connect to TronLink. Please try again.');
      // eslint-disable-next-line no-console
      console.warn('res', res);
      throw new Error('Failed to connect to TronLink. Please try again.');
    }

    return tronWeb;
  }

  const connect = async () => {
    const tronWeb = await getTronWeb();
    if (!tronWeb) {
      window.open('https://www.tronlink.org/dlDetails/', '_blank', 'noreferrer');
      return;
    }
    setTronWeb(tronWeb);
    setAddress(tronWeb.defaultAddress.base58 || null);
  };

  const disconnect = () => {
    setTronWeb(null);
    setAddress(null);
  };

  useEffect(() => {
    // Check if already connected
    if (typeof window !== 'undefined' && (window as unknown as { tronLink?: TronLink }).tronLink?.ready) {
      void connect();
    }
  }, []);

  useEffect(() => {
    const getBalance = async () => {
      if (tronWeb && address) {
        if (!balance) {
          setIsLoadingBalance(true);
        }
        const currentBalance = await tronWeb.trx.getBalance(address);
        setBalance(String(currentBalance));
        setIsLoadingBalance(false);
      }
    };

    void getBalance();

    const timer = setInterval(() => {
      void getBalance();
    }, 20_000);

    return () => clearInterval(timer);
  }, [tronWeb, address, balance]);

  const tronContextValue = useMemo(
    () => ({
      tronWeb,
      address,
      isConnected: !!address,
      connect,
      disconnect,
      balance,
      isLoadingBalance,
    }),
    [tronWeb, address, balance, isLoadingBalance]
  );

  return <TronContext.Provider value={tronContextValue}>{children}</TronContext.Provider>;
};
